import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { getClients, createClient, updateClient, deleteClient } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    website: '',
    order: 0,
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const res = await getClients();
      setClients(res.data);
    } catch (err) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingClient) {
        await updateClient(editingClient.id, formData);
        toast.success('Client updated');
      } else {
        await createClient(formData);
        toast.success('Client added');
      }
      loadClients();
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error('Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      logo: client.logo,
      website: client.website || '',
      order: client.order || 0,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      await deleteClient(id);
      toast.success('Client deleted');
      loadClients();
    } catch (err) {
      toast.error('Failed to delete client');
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({ name: '', logo: '', website: '', order: 0 });
  };

  return (
    <AdminLayout title="Client Management">
      <div className="space-y-6" data-testid="admin-clients">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-syne text-2xl font-bold text-white">Client Logos</h1>
            <p className="text-[#888] text-sm">Manage client logos displayed in the slider</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <button
                data-testid="add-client-btn"
                className="bg-[#ff5a1f] text-white px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-[#e04e1a] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Client
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-syne text-xl">
                  {editingClient ? 'Edit Client' : 'Add Client'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="client-form">
                <div>
                  <label className="block text-[#888] text-sm mb-2">Client Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="Acme Corp"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Logo URL *</label>
                  <input
                    type="url"
                    value={formData.logo}
                    onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                    required
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="https://example.com/logo.png"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Website URL</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="https://acme.com"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Display Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    className="flex-1 border border-[#333] text-white px-4 py-3 text-sm font-semibold hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-[#ff5a1f] text-white px-4 py-3 text-sm font-semibold hover:bg-[#e04e1a] flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {editingClient ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Clients Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#ff5a1f] animate-spin" />
          </div>
        ) : clients.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {clients.map((client) => (
              <div
                key={client.id}
                className="bg-[#0A0A0A] border border-[#1F1F1F] p-4"
                data-testid={`client-card-${client.id}`}
              >
                <div className="aspect-[2/1] bg-[#121212] flex items-center justify-center mb-4 overflow-hidden">
                  <img
                    src={client.logo}
                    alt={client.name}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <h3 className="text-white font-medium truncate mb-2">{client.name}</h3>
                {client.website && (
                  <a
                    href={client.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#ff5a1f] text-sm flex items-center gap-1 hover:underline mb-3"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Website
                  </a>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(client)}
                    className="flex-1 border border-[#333] text-white px-3 py-2 text-xs flex items-center justify-center gap-1 hover:border-[#ff5a1f] hover:text-[#ff5a1f]"
                  >
                    <Edit2 className="w-3 h-3" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(client.id)}
                    className="border border-[#333] text-red-500 px-3 py-2 hover:bg-red-500/10 hover:border-red-500"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-16 text-center">
            <p className="text-[#888] mb-4">No clients yet</p>
            <button
              onClick={() => setDialogOpen(true)}
              className="text-[#ff5a1f] font-semibold hover:underline"
            >
              Add your first client
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
